import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faEnvelope, faSms } from '@fortawesome/free-solid-svg-icons';
import { CommunicationService, sharedApiClient } from '../../fetcher';
import { StatusEnum } from '../../enums/statusEnum';
import '@medprompt/shared/src/common/modals.css';
import './MessagingModals.css';

// Entity types
export const EntityType = {
  DOCUMENT: 'DOCUMENT',
  CONSULTATION: 'CONSULTATION'
};

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const ViewMessagesModal = ({ 
  entityType = EntityType.DOCUMENT,
  documentId, 
  consultationId,
  entityStatus, 
  onClose 
}) => {
  // Determine the entity ID based on type
  const entityId = entityType === EntityType.CONSULTATION ? consultationId : documentId;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const communicationService = useMemo(() => new CommunicationService(sharedApiClient), []);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        let data;
        if (entityType === EntityType.CONSULTATION) {
          data = await communicationService.fetchConsultationComms(entityId);
        } else {
          data = await communicationService.fetchDocumentComms(entityId);
        }
        setMessages(data || []);
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
        console.error('Failed to fetch messages:', error);
      }
    };

    fetchMessages();
  }, [entityId, entityType]);

  // Get the communication types info
  const getCommTypesInfo = (typeToIdentifier) => {
    if (!typeToIdentifier) return [];
    
    const types = [];
    if (typeToIdentifier.TEXT) {
      types.push({
        icon: <FontAwesomeIcon icon={faSms} />,
        label: `SMS to ${typeToIdentifier.TEXT}`
      });
    }
    if (typeToIdentifier.EMAIL) {
      types.push({
        icon: <FontAwesomeIcon icon={faEnvelope} />,
        label: `Email to ${typeToIdentifier.EMAIL}`
      });
    }
    return types;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container view-messages-modal modal-animated" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Sent Messages</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-content view-messages-content">
          {loading ? (
            <div className="loading-container">
              <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
              <span>Loading messages...</span>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>Failed to load messages: {error.message || error}</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="empty-messages">
              <p>No messages have been sent for this {entityType === EntityType.CONSULTATION ? 'consultation' : 'document'}{entityStatus !== StatusEnum.FILED_AWAY.value ? ' yet' : ''}.</p>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((message) => {
                const commTypes = getCommTypesInfo(message.typeToIdentifier);
                return (
                  <div key={message.commId} className="message-item">
                    <div className="message-header">
                      <div className="message-types">
                        {commTypes.map((type, index) => (
                          <div key={index} className="message-type">
                            {type.icon}
                            <span className="message-type-label">{type.label}</span>
                          </div>
                        ))}
                        {commTypes.length === 0 && (
                          <div className="message-type">
                            <span className="message-type-label">Unknown</span>
                          </div>
                        )}
                      </div>
                      <div className="message-timestamp">
                        {formatTimestamp(message.timestamp)}
                      </div>
                    </div>
                    <div className="message-content">
                      {message.content}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ViewMessagesModal);

